import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, Image, Video, Trash2, Edit2, MapPin, Clock, User, X, Play, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

function MediaLibrary({ user }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [dragActive, setDragActive] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null); // NEW: For image/video preview

  const API_URL = import.meta.env.VITE_API_URL || window.location.origin;

  // Fetch media
  const fetchMedia = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = selectedLocation === 'all' 
        ? `${API_URL}/api/signage/media`
        : `${API_URL}/api/signage/media?location=${selectedLocation}`;
      
      const response = await axios.get(endpoint);
      setMedia(response.data);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error('Failed to load media files');
    } finally {
      setLoading(false);
    }
  }, [selectedLocation, API_URL]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  // Handle file upload
  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadPromises = Array.from(files).map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('displayLocation', selectedLocation === 'all' ? 'salon' : selectedLocation);
      formData.append('duration', 10); // Default duration for images

      try {
        await axios.post(`${API_URL}/api/signage/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(`${file.name} uploaded successfully`);
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
        toast.error(error.response?.data?.message || `Failed to upload ${file.name}`);
      }
    });

    await Promise.all(uploadPromises);
    setUploading(false);
    fetchMedia();
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  // Handle file input change
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  // Delete media
  const handleDelete = async (id, fileName) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) return;

    try {
      await axios.delete(`${API_URL}/api/signage/media/${id}`);
      toast.success('Media deleted successfully');
      fetchMedia();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast.error('Failed to delete media');
    }
  };

  // Update media
  const handleUpdate = async (id, updates) => {
    try {
      await axios.put(`${API_URL}/api/signage/media/${id}`, updates);
      toast.success('Media updated successfully');
      setEditingMedia(null);
      fetchMedia();
    } catch (error) {
      console.error('Error updating media:', error);
      toast.error('Failed to update media');
    }
  };

  // Toggle active status
  const toggleActive = async (id, currentStatus) => {
    try {
      await axios.put(`${API_URL}/api/signage/media/${id}`, {
        isActive: !currentStatus
      });
      toast.success(currentStatus ? 'Media hidden' : 'Media activated');
      fetchMedia();
    } catch (error) {
      console.error('Error toggling media status:', error);
      toast.error('Failed to update media status');
    }
  };

  // NEW: Open preview modal
  const openPreview = (item) => {
    setPreviewMedia(item);
  };

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Media Library</h2>
          <p className="text-sm text-gray-600 mt-1">
            {media.length} files • {media.filter(m => m.isActive).length} active
          </p>
        </div>
        
        {/* Location Filter */}
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700">Location:</label>
          <div className="flex gap-2">
            {['all', 'gym', 'salon'].map((loc) => (
              <button
                key={loc}
                onClick={() => setSelectedLocation(loc)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  selectedLocation === loc
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {loc.charAt(0).toUpperCase() + loc.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Upload Zone - Compact */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive
            ? 'border-purple-600 bg-purple-50'
            : 'border-gray-300 bg-gray-50 hover:border-purple-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
        <p className="text-base font-medium text-gray-700 mb-1">
          Drag & drop files or click to browse
        </p>
        <p className="text-xs text-gray-500 mb-3">
          JPG, PNG, MP4 • Max 100MB
        </p>
        <input
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,video/mp4"
          onChange={handleFileInput}
          className="hidden"
          id="file-upload"
          disabled={uploading}
        />
        <label
          htmlFor="file-upload"
          className={`inline-block px-5 py-2 bg-purple-600 text-white rounded-lg cursor-pointer hover:bg-purple-700 transition-colors text-sm font-medium ${
            uploading ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {uploading ? 'Uploading...' : 'Select Files'}
        </label>
      </div>

      {/* Media Grid - SMALLER CARDS (6-8 per row) */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-gray-50 rounded-lg">
          <div className="text-5xl mb-3">📁</div>
          <p className="text-lg font-medium">No media files found</p>
          <p className="text-sm mt-2">Upload some files to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {media.map((item) => (
            <div
              key={item._id}
              className={`group border rounded-lg overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 bg-white ${
                !item.isActive ? 'opacity-50 ring-2 ring-red-200' : 'hover:ring-2 hover:ring-purple-200'
              }`}
            >
              {/* Thumbnail - Smaller */}
              <div 
                className="relative bg-gray-100 h-36 flex items-center justify-center cursor-pointer overflow-hidden"
                onClick={() => openPreview(item)}
              >
                {item.fileType === 'image' ? (
                  <>
                    <img
                      src={`${API_URL}${item.filePath}`}
                      alt={item.fileName}
                      className="w-full h-full object-cover transition-transform group-hover:scale-110"
                    />
                    {/* Preview Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <Eye className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-center">
                      <Video className="mx-auto h-8 w-8 text-purple-400 mb-1" />
                      <p className="text-xs text-gray-600 font-medium">Video</p>
                    </div>
                    {/* Play Overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                      <Play className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </>
                )}
                
                {/* Status Badge */}
                {!item.isActive && (
                  <div className="absolute top-1 right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded font-medium">
                    Hidden
                  </div>
                )}
                
                {/* Type Badge */}
                <div className="absolute top-1 left-1">
                  {item.fileType === 'image' ? (
                    <div className="bg-blue-500 bg-opacity-90 text-white text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1">
                      <Image className="h-3 w-3" />
                    </div>
                  ) : (
                    <div className="bg-purple-500 bg-opacity-90 text-white text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1">
                      <Video className="h-3 w-3" />
                    </div>
                  )}
                </div>
              </div>

              {/* Info - Compact */}
              <div className="p-2">
                <p 
                  className="text-xs font-medium text-gray-900 truncate mb-1" 
                  title={item.fileName}
                >
                  {item.fileName}
                </p>

                {/* Metadata - Compact */}
                <div className="space-y-0.5 mb-2 text-xs text-gray-600">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {item.duration}s
                    </span>
                    <span className="text-gray-400">{item.fileSizeFormatted}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-3 w-3 mr-1" />
                    <span className="truncate">
                      {item.displayLocation.map(loc => loc.charAt(0).toUpperCase() + loc.slice(1)).join(', ')}
                    </span>
                  </div>
                </div>

                {/* Actions - Compact */}
                <div className="grid grid-cols-3 gap-1">
                  <button
                    onClick={() => toggleActive(item._id, item.isActive)}
                    className={`px-1 py-1 text-xs rounded transition-colors ${
                      item.isActive
                        ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={item.isActive ? 'Hide' : 'Show'}
                  >
                    {item.isActive ? '👁️' : '🚫'}
                  </button>
                  <button
                    onClick={() => setEditingMedia(item)}
                    className="px-1 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="h-3 w-3 mx-auto" />
                  </button>
                  <button
                    onClick={() => handleDelete(item._id, item.fileName)}
                    className="px-1 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3 mx-auto" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingMedia && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Edit Media</h3>
              <button
                onClick={() => setEditingMedia(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Preview */}
              <div className="bg-gray-100 rounded-lg p-4 text-center">
                {editingMedia.fileType === 'image' ? (
                  <img
                    src={`${API_URL}${editingMedia.filePath}`}
                    alt={editingMedia.fileName}
                    className="max-h-32 mx-auto rounded"
                  />
                ) : (
                  <div className="py-8">
                    <Video className="h-12 w-12 mx-auto text-purple-400 mb-2" />
                    <p className="text-sm text-gray-600">{editingMedia.fileName}</p>
                  </div>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Duration (seconds)
                </label>
                <input
                  type="number"
                  value={editingMedia.duration}
                  onChange={(e) => setEditingMedia({ ...editingMedia, duration: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  min="1"
                  max="300"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How long this {editingMedia.fileType} displays
                </p>
              </div>

              {/* Display Location */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Display Location
                </label>
                <div className="flex gap-4">
                  {['gym', 'salon'].map((loc) => (
                    <label key={loc} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingMedia.displayLocation.includes(loc)}
                        onChange={(e) => {
                          const newLocations = e.target.checked
                            ? [...editingMedia.displayLocation, loc]
                            : editingMedia.displayLocation.filter(l => l !== loc);
                          setEditingMedia({ ...editingMedia, displayLocation: newLocations });
                        }}
                        className="mr-2 h-4 w-4 text-purple-600 focus:ring-purple-500 rounded"
                      />
                      <span className="text-sm text-gray-700">
                        {loc.charAt(0).toUpperCase() + loc.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => handleUpdate(editingMedia._id, {
                    duration: editingMedia.duration,
                    displayLocation: editingMedia.displayLocation
                  })}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingMedia(null)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Preview Modal (Full Screen) */}
      {previewMedia && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewMedia(null)}
        >
          <button
            onClick={() => setPreviewMedia(null)}
            className="absolute top-4 right-4 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full transition-colors"
          >
            <X className="h-6 w-6 text-white" />
          </button>

          <div className="max-w-7xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {previewMedia.fileType === 'image' ? (
              <div className="flex flex-col items-center">
                <img
                  src={`${API_URL}${previewMedia.filePath}`}
                  alt={previewMedia.fileName}
                  className="max-h-[80vh] max-w-full rounded-lg shadow-2xl"
                />
                <div className="mt-4 text-center">
                  <p className="text-white font-medium text-lg">{previewMedia.fileName}</p>
                  <p className="text-gray-300 text-sm mt-1">
                    {previewMedia.fileSizeFormatted} • {previewMedia.duration}s duration
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <video
                  src={`${API_URL}${previewMedia.filePath}`}
                  controls
                  autoPlay
                  className="max-h-[80vh] max-w-full rounded-lg shadow-2xl"
                />
                <div className="mt-4 text-center">
                  <p className="text-white font-medium text-lg">{previewMedia.fileName}</p>
                  <p className="text-gray-300 text-sm mt-1">
                    {previewMedia.fileSizeFormatted} • {Math.floor(previewMedia.duration / 60)}m {previewMedia.duration % 60}s
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaLibrary;